import { sum, getPercentage } from '@/libs/utils'
import { parse } from 'date-fns'

function getRankData (dailyData, paths) {
  const data = {}

  function addData (key, value) {
    if (!data[key]) {
      data[key] = 0
    }
    data[key] += value
  }

  function walker (node, depth, currentPaths) {
    if (!node) {
      return
    }

    if (typeof node === 'object') {
      const path = paths[depth]
      const isLastPath = depth === paths.length - 1

      if (path.charAt(0) === '{') {
        for (const [key, value] of Object.entries(node)) {
          if (isLastPath) {
            if (key !== '_c') {
              const dataKey = currentPaths.concat([key]).join('|')
              addData(dataKey, value['_c'])
            }
          } else {
            walker(node[key], depth + 1, currentPaths.concat([key]))
          }
        }
      } else if (path === '*') {
        for (const key of Object.keys(node)) {
          if (key !== 'c') {
            walker(node[key], depth + 1, currentPaths.concat(['*']))
          }
        }
      } else if (depth === paths.length - 1) {
        const dataKey = currentPaths.concat([path]).join('|')
        addData(dataKey, node[path]['_c'])
      } else {
        walker(node[path], depth + 1, currentPaths.concat([path]))
      }
    }
  }
  for (const item of dailyData) {
    walker(item, 0, [])
  }
  return data
}

function getTopData (data, topSize) {
  const items = Object.entries(data)
  items.sort((a, b) => {
    return b[1] - a[1]
  })

  const topItems = items.slice(0, topSize)
  const etcItems = items.slice(topSize)
  const topCount = sum(topItems, 1)
  let etcCount = 0

  if (etcItems.length) {
    etcCount = sum(etcItems, 1)
  }

  const count = topCount + etcCount

  for (const item of topItems) {
    item.push(getPercentage(item[1], count))
  }

  return {
    topItems,
    etcCount,
    topCount,
    count,
    etcPercent: getPercentage(etcCount, count)
  }
}

function getTargetNode (node, paths) {
  let target = node

  for (const path of paths) {
    if (target[path]) {
      target = target[path]
    } else {
      return null
    }
  }
  return target
}

function _getCount (dailyItem, key) {
  const paths = key.split('|')
  let target = getTargetNode(dailyItem, paths)

  if (target) {
    return target['_c'] || 0
  }
  return 0
}

function getCount (dailyItem, key) {
  const targetPaths = []
  let hasAllPath = false
  const paths = key.split('|')

  paths.forEach((path, index) => {
    if (path === '*') {
      hasAllPath = true
      const prevPath = paths.slice(0, index)
      const target = getTargetNode(dailyItem, prevPath)

      if (target) {
        for (const key of Object.keys(target)) {
          if (key !== '_c') {
            const newPath = prevPath.concat([key]).concat(paths.slice(index + 1))
            targetPaths.push(newPath.join('|'))
          }
        }
      }
    }
  })

  if (!hasAllPath) {
    targetPaths.push(key)
  }

  let count = 0

  for (const path of targetPaths) {
    count += _getCount(dailyItem, path)
  }

  return count
}

function getDailyTopChartData (dailyData, topData, countKey, addEtc) {
  const data = []
  const dailyCounts = []

  for (const dailyItem of dailyData) {
    data.push([])
    dailyCounts.push(getCount(dailyItem, countKey))

    for (const item of topData.topItems) {
      const itemCount = getCount(dailyItem, item[0])
      data[data.length - 1].push(itemCount)
    }
  }

  const percentageData = data.map((dataItem, i) => {
    return dataItem.map(item => {
      return getPercentage(item, dailyCounts[i])
    })
  })

  if (addEtc) {
    for (let i = 0; i < data.length; i++) {
      const dataItem = data[i]
      const topCount = sum(dataItem)
      const total = dailyCounts[i]
      const etcPercentage = getPercentage(total - topCount, total)
      percentageData[i].push(etcPercentage)
    }
  }

  return percentageData
}

function transpose (arr) {
  return Object.keys(arr[0]).map(col => {
    return arr.map(row => row[col])
  })
}

export function getDailyData (dailyData, paths, countPaths, topSize, addEtc) {
  const rankData = getRankData(dailyData, paths)
  const topData = getTopData(rankData, topSize)
  const chartData = getDailyTopChartData(dailyData, topData, countPaths.join('|'), addEtc)
  return {
    topData,
    chartData: transpose(chartData),
    timestamps: dailyData.map(item => parse(item.day).getTime())
  }
}

// todo: i18n check
export const deviceOptions = [
  {
    text: 'Device - All',
    value: null
  },
  {
    text: 'Desktop',
    value: 'desktop'
  },
  {
    text: 'Tablet',
    value: 'tablet'
  },
  {
    text: 'Phone',
    value: 'phone'
  }
]

export const osDeviceOptions = [
  {
    value: 'Device - All',
    paths: ['device', '{device}', '{os}'],
    countPaths: ['device']
  },
  {
    value: 'Desktop',
    paths: ['device', 'desktop', '{os}'],
    countPaths: ['device', 'desktop']
  },
  {
    value: 'Tablet',
    paths: ['device', 'tablet', '{os}'],
    countPaths: ['device', 'tablet']
  },
  {
    value: 'Phone',
    paths: ['device', 'phone', '{os}'],
    countPaths: ['device', 'phone']
  }
]

export const browserDeviceOptions = [
  {
    value: 'Device/OS - All',
    paths: ['device', '{device}', '{os}', '*', '{browser}'],
    countPaths: ['device']
  },
  {
    value: 'Desktop > All',
    paths: ['device', 'desktop', '{os}', '*', '{browser}'],
    countPaths: ['device', 'desktop']
  },
  {
    value: 'Desktop > Windows',
    paths: ['device', 'desktop', 'Windows', '*', '{browser}'],
    countPaths: ['device', 'desktop', 'Windows']
  },
  {
    value: 'Desktop > Mac OS',
    paths: ['device', 'desktop', 'Mac OS', '*', '{browser}'],
    countPaths: ['device', 'desktop', 'Mac OS']
  },
  {
    value: 'Desktop > Linux',
    paths: ['device', 'desktop', 'Linux', '*', '{browser}'],
    countPaths: ['device', 'desktop', 'Linux']
  },
  {
    value: 'Tablet > All',
    paths: ['device', 'tablet', '{os}', '*', '{browser}'],
    countPaths: ['device', 'tablet']
  },
  {
    value: 'Phone > All',
    paths: ['device', 'phone', '{os}', '*', '{browser}'],
    countPaths: ['device', 'phone']
  },
  {
    value: 'Phone > iOS',
    paths: ['device', 'phone', 'iOS', '*', '{browser}'],
    countPaths: ['device', 'phone', 'iOS']
  },
  {
    value: 'Phone > Android',
    paths: ['device', 'phone', 'Android', '*', '{browser}'],
    countPaths: ['device', 'phone', 'Android']
  }
]

export function getConversionUrl (url, hostname) {
  url = url.trim()
  const regex = /^https?:\/\//i
  const isAbsoluteUrl = regex.test(url)

  if (!isAbsoluteUrl) {
    if (url.charAt(0) !== '/') {
      url = '/' + url
    }
    url = `http://${hostname}${url}`
  }
  return url
}

export function getBrowserIconName (value) {
  value = String(value).toLowerCase().replace(/webview|browser|mobile|\s/g, '')
  switch (value) {
    case 'chrome':
    case 'edge':
    case 'facebook':
    case 'firefox':
    case 'ie':
    case 'safari':
    case 'opera':
    case 'android':
      return value
    default:
      return 'etc'
  }
}

export function getOsIconName (value) {
  value = String(value).toLowerCase().replace(/mobile|\s/g, '')
  switch (value) {
    case 'macos':
    case 'windows':
    case 'linux':
    case 'ios':
    case 'android':
      return value
    default:
      return 'etc'
  }
}
